/**
 * useRoundIndex — the Tablero's round index, kept fresh (issue #334).
 *
 * Thin React binding over `startRoundIndexSync` (indexSync.ts, where the rules
 * live and are tested). One module-level cache survives navigation between the
 * Tablero and the detail, so coming back renders instantly and revalidates in
 * the background. DEMO mode serves the synthetic index and never polls.
 */

import { useCallback, useEffect, useRef, useState } from "react";

import { IS_DEMO, type ExpedienteIndexRow } from "../../api/client";
import { DEMO_INDEX } from "./demoIndex";
import { fetchRoundIndex } from "./fetchIndex";
import {
  documentVisibility,
  RoundIndexCache,
  startRoundIndexSync,
  type RoundIndexSync,
} from "./indexSync";

const roundIndexCache = new RoundIndexCache();

export type RoundIndexState = {
  rows: ExpedienteIndexRow[] | null;
  /** Only a failure with nothing to show; background probe errors are silent
   * while an index is on screen (the next probe retries). */
  error: Error | null;
  loading: boolean;
  /** Manual reload: revalidate now. */
  reload: () => void;
};

export function useRoundIndex(
  roundId: string,
  /** Called when a probe replaced the on-screen index (a 200 after a 200). */
  onChange?: () => void,
): RoundIndexState {
  const [rows, setRows] = useState<ExpedienteIndexRow[] | null>(
    IS_DEMO ? DEMO_INDEX : (roundIndexCache.get(roundId)?.items ?? null),
  );
  const [error, setError] = useState<Error | null>(null);
  const syncRef = useRef<RoundIndexSync | null>(null);
  // Latest callback without restarting the sync when the parent re-renders.
  const onChangeRef = useRef(onChange);
  onChangeRef.current = onChange;

  useEffect(() => {
    if (IS_DEMO) {
      setRows(DEMO_INDEX);
      return;
    }
    setRows(roundIndexCache.get(roundId)?.items ?? null);
    setError(null);
    const sync = startRoundIndexSync({
      roundId,
      fetcher: fetchRoundIndex,
      cache: roundIndexCache,
      visibility: documentVisibility,
      onData: (items) => {
        setRows(items);
        setError(null);
      },
      onChange: () => onChangeRef.current?.(),
      onError: (err) =>
        setError(err instanceof Error ? err : new Error(String(err))),
    });
    syncRef.current = sync;
    return () => {
      sync.stop();
      syncRef.current = null;
    };
  }, [roundId]);

  const reload = useCallback(() => {
    setError(null);
    syncRef.current?.refresh();
  }, []);

  return {
    rows,
    error: rows ? null : error,
    loading: rows === null && error === null,
    reload,
  };
}
